/** @format */

import { useEffect, useRef, useState } from 'react';

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Smoothly animates toward a numeric target (e.g. progress percent).
 * Returns null while target is null/undefined.
 */
export function useAnimatedNumber(
  target: number | null | undefined,
  { durationMs = 280 }: { durationMs?: number } = {}
): number | null {
  const [display, setDisplay] = useState<number | null>(() =>
    typeof target === 'number' ? target : null
  );
  const displayRef = useRef(typeof target === 'number' ? target : 0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof target !== 'number' || Number.isNaN(target)) {
      setDisplay(null);
      return undefined;
    }

    const from = displayRef.current;
    const to = target;
    if (from === to) {
      setDisplay(to);
      return undefined;
    }

    const distance = Math.abs(to - from);
    const duration = Math.min(durationMs, Math.max(100, distance * 14));
    const start = performance.now();

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const value = Math.round(from + (to - from) * easeOutCubic(t));
      displayRef.current = value;
      setDisplay(value);
      if (t < 1) {
        rafRef.current = window.requestAnimationFrame(tick);
      }
    };

    rafRef.current = window.requestAnimationFrame(tick);

    return () => {
      if (rafRef.current != null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [target, durationMs]);

  return display;
}

export { easeOutCubic };
